import { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import CapiTestEventResult from "routes/meta_leads/CapiTestEventResult";
import MetaAdAccountService from "services/MetaAdAccount";

const CapiHealthCheckCard = ({ accountId, datasets }) => {
  const [datasetId, setDatasetId] = useState("");
  const [eventName, setEventName] = useState("Lead");
  const [actionSource, setActionSource] = useState("system_generated");
  const [accessToken, setAccessToken] = useState("");
  const [testEventCode, setTestEventCode] = useState("");
  const [result, setResult] = useState(null);

  const mutation = useMutation({
    mutationFn: () =>
      MetaAdAccountService.sendTestEvent(
        accountId,
        datasetId,
        testEventCode,
        eventName,
        accessToken,
        actionSource
      ),
    onSuccess: response => setResult({ ok: true, body: response }),
    onError: err =>
      setResult({
        ok: false,
        body: err.response?.data ?? err.body ?? { message: String(err) },
      }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Conversions API health check</CardTitle>
        <CardDescription>
          Send a single synthetic event to confirm the account, token, and
          dataset are connected. Paste the test event code from the Events
          Manager Test Events tab; the event appears there and never reaches
          production optimization.
        </CardDescription>
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:gap-4">
        <Field className="tw:w-80">
          <FieldLabel htmlFor="capi-healthcheck-dataset">Dataset</FieldLabel>
          <Select value={datasetId} onValueChange={setDatasetId}>
            <SelectTrigger id="capi-healthcheck-dataset" className="tw:w-full">
              <SelectValue placeholder="Choose a dataset">
                {value =>
                  value
                    ? (datasets.find(d => d.dataset_id === value)?.name ??
                      value)
                    : "Choose a dataset"
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {datasets.map(dataset => (
                <SelectItem key={dataset.id} value={dataset.dataset_id}>
                  {dataset.name || dataset.dataset_id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field className="tw:w-80">
          <FieldLabel htmlFor="capi-healthcheck-event-name">
            Event name
          </FieldLabel>
          <Input
            id="capi-healthcheck-event-name"
            value={eventName}
            onChange={event => setEventName(event.target.value)}
            placeholder="Lead"
          />
        </Field>

        <Field className="tw:w-80">
          <FieldLabel htmlFor="capi-healthcheck-action-source">
            action_source
          </FieldLabel>
          <Select value={actionSource} onValueChange={setActionSource}>
            <SelectTrigger
              id="capi-healthcheck-action-source"
              className="tw:w-full"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="system_generated">system_generated</SelectItem>
              <SelectItem value="website">website</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <Field className="tw:w-80">
          <FieldLabel htmlFor="capi-healthcheck-token">Access token</FieldLabel>
          <Input
            id="capi-healthcheck-token"
            type="password"
            value={accessToken}
            onChange={event => setAccessToken(event.target.value)}
            placeholder="Optional dataset token"
          />
          <span className="tw:text-sm tw:text-[var(--color-text-muted)]">
            Optional. Paste a dataset token from Events Manager; blank uses the
            account token.
          </span>
        </Field>

        <Field className="tw:w-80">
          <FieldLabel htmlFor="capi-healthcheck-code">
            Test event code
          </FieldLabel>
          <Input
            id="capi-healthcheck-code"
            value={testEventCode}
            onChange={event => setTestEventCode(event.target.value)}
            placeholder="TEST12345"
          />
        </Field>

        <div>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!datasetId || !testEventCode || mutation.isPending}
          >
            {mutation.isPending && <Spinner />}
            Send test event
          </Button>
        </div>

        {result && <CapiTestEventResult result={result} />}
      </CardContent>
    </Card>
  );
};

export default CapiHealthCheckCard;
